"use client";

import {
  Box, Typography, Paper, Chip, Table, TableHead, TableRow,
  TableCell, TableBody, TablePagination, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Divider, Grid, CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { formatISTDateTime, maskPhone } from "../../helper";

const fmt = (v) => `${Number(v || 0).toLocaleString("en-IN")}`;

const SummaryCard = ({ label, value, color = "text.primary", bg = "#f5f7fa" }) => (
  <Paper sx={{ p: 1.5, background: bg, borderRadius: 2 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="subtitle1" fontWeight={700} color={color} mt={0.3}>{value}</Typography>
  </Paper>
);

const DenomTable = ({ title, denoms }) => {
  const entries = Object.entries(denoms || {}).filter(([, q]) => Number(q) > 0);
  if (!entries.length) return null;
  const total = entries.reduce((s, [d, q]) => s + Number(d) * Number(q), 0);
  return (
    <Box mb={2}>
      <Typography fontWeight={700} mb={0.5} variant="body2">{title}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#f4f6f8" }}>
            <TableCell align="center" sx={{ fontWeight: 700 }}>Denom</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>Qty</TableCell>
            <TableCell align="right"  sx={{ fontWeight: 700 }}>Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([d, q]) => (
            <TableRow key={d} hover>
              <TableCell align="center">{Number(d).toLocaleString()}</TableCell>
              <TableCell align="center">{q}</TableCell>
              <TableCell align="right">{(Number(d) * Number(q)).toLocaleString()}</TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ background: "#f9f9f9" }}>
            <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>{fmt(total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const TXN_COLOR = {
  DEPOSIT: "#388e3c", WITHDRAW: "#d32f2f", CHIP_ISSUE: "#1976d2",
  CHIP_CASHOUT: "#f57c00", LOAN_DISBURSE: "#7b1fa2", LOAN_REPAY: "#546e7a",
  CHIP_EXCHANGE: "#00838f", RAKE: "#5d4037", REFERRAL_CASHBACK: "#ad1457",
  FLOAT_REPAY: "#e65100",
};

const FLOAT_SOURCE_LABEL = {
  CASHOUT: "Chip Cash-Out", CHIP_RETURN: "Chips Returned",
  DEPOSIT_CASH: "Deposit (Cash)", DEPOSIT_BANK: "Deposit (Bank)", DEPOSIT_OTHER: "Deposit (Other)",
};

export default function LedgerPage() {
  const token = useSelector((s) => s.loginSlice.token);

  const [ledgerList, setLedgerList]   = useState([]);
  const [pagination, setPagination]   = useState({});
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [detailOpen, setDetailOpen]   = useState(false);
  const [detail, setDetail]           = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { fetchLedger(); }, [page, rowsPerPage]);

  const fetchLedger = async () => {
    const res = await axiosRequest("GET", `/shift-management?page=${page + 1}&size=${rowsPerPage}`, token);
    setLedgerList(res?.data?.records || []);
    setPagination(res?.data?.pagination || {});
  };

  const openDetail = async (record) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    const res = await axiosRequest("GET", `/shift-management/${record._id}/detail`, token);
    setDetail(res?.data || null);
    setDetailLoading(false);
  };

  const shift = detail?.shift;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      <Typography variant="h4" fontWeight={600} mb={3}>Cashier Ledger</Typography>

      <Paper elevation={4} sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Table>
          <TableHead sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)" }}>
            <TableRow>
              {["Cashier", "Opening Cash", "Opening Chips", "Shift Start", "Status", "Action"].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, color: "#fff" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ledgerList.map((r) => (
              <TableRow key={r._id} hover sx={{ background: r.source === "TG" ? "#f9f9ff" : undefined }}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {r.source === "TG" && (
                      <Chip label="TG" size="small" color="primary" variant="outlined"
                        sx={{ fontSize: "0.65rem", height: 18, px: 0.3 }} />
                    )}
                    {r.email || "-"}
                  </Box>
                </TableCell>
                <TableCell>{fmt(r.opening_cash)}</TableCell>
                <TableCell>{fmt(r.opening_chips)}</TableCell>
                <TableCell>{formatISTDateTime(r.date_time)}</TableCell>
                <TableCell>
                  <Chip label={r.shift_status ? "CLOSED" : "OPEN"} size="small"
                    color={r.shift_status ? "default" : "success"} />
                </TableCell>
                <TableCell>
                  {r.source === "TG" ? (
                    <Chip label="Telegram Shift" size="small" variant="outlined" color="primary" />
                  ) : (
                    <Button variant="outlined" size="small" onClick={() => openDetail(r)}>
                      View Session
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {ledgerList.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">No Records Found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={pagination?.total || 0} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      {/* ── SESSION DETAIL DIALOG ── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3 } }}>

        <Box sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)", px: 3, py: 2, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Session Detail</Typography>
            {shift && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {shift.email} &nbsp;|&nbsp; {formatISTDateTime(detail?.period?.start)} → {shift.shift_status ? formatISTDateTime(detail?.period?.end) : "Ongoing"}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {shift && <Chip label={shift.shift_status ? "CLOSED" : "OPEN"} size="small" color={shift.shift_status ? "default" : "success"} />}
            <IconButton onClick={() => setDetailOpen(false)} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {detailLoading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}

          {!detailLoading && detail && (
            <>
              {/* ── OPENING vs CLOSING ── */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#42a5f5", background: "#f0f8ff" }}>
                    <Typography fontWeight={700} color="#1565c0" mb={1}>Opening Balance</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}><SummaryCard label="Cash" value={fmt(shift?.opening_cash)} color="#1565c0" bg="#e3f2fd" /></Grid>
                      <Grid item xs={6}><SummaryCard label="Chips Value" value={fmt(shift?.opening_chips)} color="#1565c0" bg="#e3f2fd" /></Grid>
                      <Grid item xs={6}><SummaryCard label="Winning Balance" value={fmt(shift?.winning_balance)} bg="#e3f2fd" /></Grid>
                      <Grid item xs={6}><SummaryCard label="Cash Deposite" value={fmt(shift?.cash_deposite)} bg="#e3f2fd" /></Grid>
                    </Grid>
                    <Box mt={1.5}>
                      <DenomTable title="Opening Chip Denominations" denoms={shift?.chip_denominations} />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: shift?.shift_status ? "#66bb6a" : "#ffa726", background: shift?.shift_status ? "#f1fff3" : "#fffbf0" }}>
                    <Typography fontWeight={700} color={shift?.shift_status ? "#2e7d32" : "#e65100"} mb={1}>
                      {shift?.shift_status ? "Closing Balance" : "Current Balance (Live)"}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}><SummaryCard label="Cash" value={fmt(detail?.closing_cash)} color="#2e7d32" bg="#e8f5e9" /></Grid>
                      <Grid item xs={6}><SummaryCard label="Bank" value={fmt(detail?.closing_bank)} color="#e65100" bg="#fff8e1" /></Grid>
                      <Grid item xs={6}><SummaryCard label="Chips Value" value={fmt(detail?.closing_chips)} color="#2e7d32" bg="#e8f5e9" /></Grid>
                      <Grid item xs={6}><SummaryCard label="Cash Deposited" value={fmt(detail?.cash_deposit)} bg="#e8f5e9" /></Grid>
                      <Grid item xs={6}><SummaryCard label="Cash Withdrawn" value={fmt(detail?.cash_withdraw)} bg="#fce4ec" color="#c62828" /></Grid>
                      <Grid item xs={6}><SummaryCard label="Bank Deposited" value={fmt(detail?.bank_deposit)} bg="#fff8e1" color="#e65100" /></Grid>
                      <Grid item xs={6}><SummaryCard label="Bank Withdrawn" value={fmt(detail?.bank_withdraw)} bg="#fce4ec" color="#c62828" /></Grid>
                    </Grid>
                    <Box mt={1.5}>
                      <DenomTable title="Closing Chip Denominations" denoms={detail?.closing_chip_denominations} />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* ── ON TABLE CHIPS ── */}
              {detail?.on_table_chips && Object.keys(detail.on_table_chips).filter(k => Number(detail.on_table_chips[k]) > 0).length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography fontWeight={700} mb={1}>On Table Chips</Typography>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: "#f5f5f5" }}>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Denomination</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Quantity</TableCell>
                          <TableCell align="right"  sx={{ fontWeight: 700 }}>Total Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(detail.on_table_chips)
                          .filter(([, q]) => Number(q) > 0)
                          .sort(([a], [b]) => Number(a) - Number(b))
                          .map(([denomination, quantity]) => (
                            <TableRow key={denomination} hover>
                              <TableCell align="center">
                                <Typography fontWeight={600}>{Number(denomination).toLocaleString()}</Typography>
                              </TableCell>
                              <TableCell align="center">{quantity}</TableCell>
                              <TableCell align="right">
                                {(Number(denomination) * Number(quantity)).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        <TableRow sx={{ background: "#f9f9f9" }}>
                          <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                            {fmt(Object.entries(detail.on_table_chips).reduce((s, [d, q]) => s + Number(d) * Number(q), 0))}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                </>
              )}

              {/* ── SESSION SUMMARY ── */}
              <Typography fontWeight={700} mb={1}>Session Transaction Summary</Typography>
              <Grid container spacing={1.5} mb={3}>
                {[
                  { label: "Total Deposits",      value: fmt(detail?.summary?.deposit),       color: "#388e3c", bg: "#f1fff3" },
                  { label: "Total Withdrawals",   value: fmt(detail?.summary?.withdraw),      color: "#d32f2f", bg: "#fff5f5" },
                  { label: "Chip Issues",         value: fmt(detail?.summary?.chip_issue),    color: "#1976d2", bg: "#f0f4ff" },
                  { label: "Chip Cashouts",       value: fmt(detail?.summary?.chip_cashout),  color: "#f57c00", bg: "#fff8f0" },
                  { label: "Chip Exchanges",      value: fmt(detail?.summary?.chip_exchange), color: "#0097a7", bg: "#e0f7fa" },
                  { label: "Rake Collected",      value: fmt(detail?.summary?.rake),          color: "#5d4037", bg: "#fdf5f0" },
                  { label: "Loans Disbursed",     value: fmt(detail?.summary?.loan_disburse),         color: "#7b1fa2", bg: "#fdf0ff" },
                  { label: "Offer Distributed",   value: fmt(detail?.summary?.offer_distributed),     color: "#ad1457", bg: "#fce4ec" },
                  { label: "Float Issued",        value: fmt(detail?.summary?.float_issued),          color: "#e65100", bg: "#fff3e0" },
                  { label: "Float Repaid",        value: fmt(detail?.summary?.float_repaid),          color: "#2e7d32", bg: "#e8f5e9" },
                ].map(({ label, value, color, bg }) => (
                  <Grid item xs={6} sm={4} md={2} key={label}>
                    <SummaryCard label={label} value={value} color={color} bg={bg} />
                  </Grid>
                ))}
              </Grid>

              {/* ── REFILL SECTION ── */}
              {detail?.refills?.count > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography fontWeight={700}>
                      Counter Refills ({detail.refills.count})
                    </Typography>
                  </Box>
                  <Grid container spacing={1.5} mb={2}>
                    <Grid item xs={6} sm={3}>
                      <SummaryCard label="Refill Cash" value={fmt(detail.refills.total_cash)} color="#1565c0" bg="#e3f2fd" />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <SummaryCard label="Refill Chips Value" value={fmt(detail.refills.total_chips)} color="#1565c0" bg="#e3f2fd" />
                    </Grid>
                  </Grid>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: "#e3f2fd" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Cash</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Chip Denominations</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Chips Value</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Added By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.refills.records.map((r) => {
                          const denoms = r.chip_denominations instanceof Map
                            ? Object.fromEntries(r.chip_denominations)
                            : (r.chip_denominations || {});
                          const denomStr = Object.entries(denoms)
                            .filter(([, q]) => Number(q) > 0)
                            .sort(([a], [b]) => Number(a) - Number(b))
                            .map(([d, q]) => `${d}×${q}`)
                            .join(" | ") || "—";
                          return (
                            <TableRow key={r._id} hover>
                              <TableCell sx={{ fontSize: 11 }}>{formatISTDateTime(r.date_time)}</TableCell>
                              <TableCell align="right">{fmt(r.cash_amount)}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{denomStr}</TableCell>
                              <TableCell align="right">{fmt(r.total_chips)}</TableCell>
                              <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{r.note || "—"}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{r.email || "—"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>
                </>
              )}

              {/* ── CHIP EXCHANGE SECTION ── */}
              {detail?.chip_exchanges?.count > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography fontWeight={700}>
                      Chip Exchanges ({detail.chip_exchanges.count})
                    </Typography>
                    <SummaryCard label="Total Exchanged" value={fmt(detail.chip_exchanges.total)} color="#0097a7" bg="#e0f7fa" />
                  </Box>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: "#e0f7fa" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Chips Given</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Chips Received</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Value</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Done By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.chip_exchanges.records.map((r) => {
                          const fmtChips = (arr) => (arr || [])
                            .filter(c => c.quantity > 0)
                            .sort((a, b) => a.denomination - b.denomination)
                            .map(c => `${c.denomination}×${c.quantity}`)
                            .join(" | ") || "—"
                          return (
                            <TableRow key={r._id} hover>
                              <TableCell sx={{ fontSize: 11 }}>{formatISTDateTime(r.date)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{r.user_name || "—"}</Typography>
                                <Typography variant="caption" color="text.secondary">{maskPhone(r.phone) || r.player_id || ""}</Typography>
                              </TableCell>
                              <TableCell sx={{ fontSize: 12, color: "#d32f2f" }}>{fmtChips(r.chips_given)}</TableCell>
                              <TableCell sx={{ fontSize: 12, color: "#1976d2" }}>{fmtChips(r.chips_received)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: "#0097a7" }}>{fmt(r.total_given)}</TableCell>
                              <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{r.remarks || "—"}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{r.created_by?.email || r.created_by?.name || "—"}</TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </Paper>
                </>
              )}

              {/* ── FLOAT EXCHANGE SECTION ── */}
              {(detail?.float_exchange?.issued?.count > 0 || detail?.float_exchange?.repaid?.count > 0 || detail?.float_exchange?.outstanding?.total > 0) && (() => {
                const fx = detail.float_exchange;
                return (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography fontWeight={700} mb={1}>Float Exchange</Typography>
                  <Grid container spacing={1.5} mb={2}>
                    <Grid item xs={6} sm={3}><SummaryCard label={`Issued (${fx.issued.count})`} value={fmt(fx.issued.total)} color="#e65100" bg="#fff3e0" /></Grid>
                    <Grid item xs={6} sm={3}><SummaryCard label={`Repaid (${fx.repaid.count})`} value={fmt(fx.repaid.total)} color="#2e7d32" bg="#e8f5e9" /></Grid>
                    <Grid item xs={6} sm={3}><SummaryCard label={`Carried In (${fx.carried_in.count})`} value={fmt(fx.carried_in.total)} /></Grid>
                    <Grid item xs={6} sm={3}><SummaryCard label={`Outstanding at End (${fx.outstanding.count})`} value={fmt(fx.outstanding.total)} color="#d32f2f" bg="#fff5f5" /></Grid>
                  </Grid>
                  {Object.keys(fx.repaid.by_source || {}).length > 0 && (
                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      {Object.entries(fx.repaid.by_source).map(([k, v]) => (
                        <Chip key={k} size="small" variant="outlined" label={`${FLOAT_SOURCE_LABEL[k] || k}: ${fmt(v)}`} />
                      ))}
                    </Box>
                  )}
                  {(fx.issued.records.length > 0 || fx.repaid.records.length > 0) && (
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: "#fff3e0" }}>
                            <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Entry</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[
                            ...fx.issued.records.map((r) => ({
                              key: `i${r._id}`, date: r.createdAt, name: r.user_name, phone: r.phone, player_id: r.player_id,
                              entry: "Issued", color: "#e65100", amount: r.total_amount,
                              detail: (r.chips || []).map(c => `${c.denomination}×${c.quantity}`).join(" | ") || "—",
                            })),
                            ...fx.repaid.records.map((r, i) => ({
                              key: `r${i}`, date: r.date, name: r.user_name, phone: r.phone, player_id: r.player_id,
                              entry: "Repaid", color: "#2e7d32", amount: r.amount,
                              detail: FLOAT_SOURCE_LABEL[r.source === "DEPOSIT" ? `DEPOSIT_${r.payment_mode || "CASH"}` : r.source] || r.source,
                            })),
                          ].sort((a, b) => new Date(a.date) - new Date(b.date)).map((row) => (
                            <TableRow key={row.key} hover>
                              <TableCell sx={{ fontSize: 11 }}>{formatISTDateTime(row.date)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{row.name || "—"}</Typography>
                                <Typography variant="caption" color="text.secondary">{maskPhone(row.phone) || row.player_id || ""}</Typography>
                              </TableCell>
                              <TableCell sx={{ color: row.color, fontWeight: 700 }}>{row.entry}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{row.detail}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: row.color }}>{fmt(row.amount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </>
                );
              })()}

              <Divider sx={{ my: 2 }} />

              {/* ── ALL TRANSACTIONS ── */}
              <Typography fontWeight={700} mb={1}>
                All Transactions ({detail?.transactions?.length || 0})
              </Typography>
              {detail?.transactions?.length > 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: "#f4f6f8" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Txn ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.transactions.map((t) => (
                        <TableRow key={t._id} hover>
                          <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>{t.local_txn_id}</TableCell>
                          <TableCell>
                            <Chip label={t.txn_type} size="small"
                              sx={{ background: TXN_COLOR[t.txn_type] || "#999", color: "#fff", fontWeight: 700, fontSize: 10 }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{t.user_id?.name || "—"}</Typography>
                            <Typography variant="caption" color="text.secondary">{maskPhone(t.user_id?.phone) || t.user_id?.playerId || ""}</Typography>
                          </TableCell>
                          <TableCell>{t.payment_mode || "—"}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: ["DEPOSIT","CHIP_CASHOUT","REFERRAL_CASHBACK"].includes(t.txn_type) ? "#388e3c" : "#d32f2f" }}>
                            {fmt(t.txn_amount)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{formatISTDateTime(t.txn_date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              ) : (
                <Typography color="text.secondary" variant="body2">No transactions in this session.</Typography>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
